import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Loader2, RefreshCw, Save, ShieldCheck, Users } from "lucide-react";
import { apiGet, apiPut } from "@/utils/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { PAGE_GROUPS } from "@/utils/access";
import { toast } from "sonner";

const FILTERS = ["All users", "Pending access", "Users", "Admins"];

export default function AccessManagement() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("All users");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try { const data = await apiGet("/access-management/users"); setUsers(data.users || []); }
    catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const shownUsers = useMemo(() => users.filter((user) => {
    if (filter === "Pending access") return user.pending_access;
    if (filter === "Users") return user.role === "user";
    if (filter === "Admins") return user.role !== "user";
    return true;
  }), [users, filter]);

  const saveAccess = async (user) => {
    setSaving((state) => ({ ...state, [user.user_id]: true }));
    try {
      const updated = await apiPut(`/access-management/users/${user.user_id}`, { allowed_pages: user.allowed_pages });
      setUsers((items) => items.map((item) => item.user_id === user.user_id ? updated : item));
      toast.success(`Access saved for ${user.display_name || user.email}`);
    } catch (error) { toast.error(error.message); }
    finally { setSaving((state) => ({ ...state, [user.user_id]: false })); }
  };

  const togglePage = (userId, pageId, enabled) => setUsers((items) => items.map((user) => {
    if (user.user_id !== userId) return user;
    const pages = new Set(user.allowed_pages || []);
    enabled ? pages.add(pageId) : pages.delete(pageId);
    return { ...user, allowed_pages: [...pages], pending_access: pages.size === 0 };
  }));

  return (
    <div className="max-w-[1500px] mx-auto space-y-5" data-testid="access-management-page">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#0A838A]"><ShieldCheck size={15} /><p className="text-[11px] font-bold uppercase tracking-wide">IT Access Management</p></div>
          <h2 className="mt-1 text-xl font-heading font-bold text-slate-900">User access</h2>
          <p className="mt-1 text-[13px] text-slate-600">Grant or revoke page-level access for people who have signed in with Microsoft.</p>
        </div>
        <Button variant="outline" onClick={loadUsers} data-testid="refresh-access-users-button" className="font-bold"><RefreshCw size={14} /> Refresh</Button>
      </div>

      <div className="flex items-center gap-3 border-b border-slate-200 pb-4">
        <label htmlFor="access-filter" className="text-[11px] font-bold uppercase text-slate-600">Filter by access</label>
        <select id="access-filter" value={filter} onChange={(event) => setFilter(event.target.value)} data-testid="access-filter-select" className="h-9 min-w-[170px] border border-slate-300 bg-white px-3 text-[12px] font-bold text-slate-700">
          {FILTERS.map((item) => <option key={item}>{item}</option>)}
        </select>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 size={20} className="mx-auto animate-spin text-[#0A838A]" /></div> : shownUsers.length ? shownUsers.map((user) => (
        <UserAccessRow key={user.user_id} user={user} saving={saving[user.user_id]} onToggle={togglePage} onSave={saveAccess} />
      )) : <div className="py-14 border border-dashed border-slate-300 text-center text-sm text-slate-500" data-testid="empty-access-list">No users match this filter.</div>}
    </div>
  );
}

function UserAccessRow({ user, saving, onToggle, onSave }) {
  const fullAccess = user.role === "admin" || user.role === "super_admin";
  return (
    <section className="border-y border-slate-200 bg-white px-5 py-4" data-testid={`access-user-${user.user_id}`}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2"><h3 className="text-[14px] font-bold text-slate-900">{user.display_name || "New Microsoft user"}</h3><RoleBadge role={user.role} userId={user.user_id} /></div>
          <p className="mt-0.5 text-[12px] text-slate-500">{user.email}</p>
          {user.pending_access && <p className="mt-1 text-[11px] font-bold text-amber-700" data-testid={`pending-user-${user.user_id}`}>Pending access — no pages granted</p>}
        </div>
        {!fullAccess && <Button onClick={() => onSave(user)} disabled={saving} data-testid={`save-access-${user.user_id}`} className="h-8 bg-[#0A838A] hover:bg-[#076D74] text-white text-xs font-bold">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}{saving ? "Saving…" : "Save access"}</Button>}
      </div>
      {fullAccess ? <p className="mt-4 border-l-2 border-[#0A838A] bg-[#F2F9F8] px-3 py-2 text-[12px] font-bold text-[#076D74]">{user.role === "super_admin" ? "Super administrators" : "Administrators"} automatically have access to every page.</p> : <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-4">
        {PAGE_GROUPS.map((group) => <div key={group.label}><p className="text-[10px] font-bold uppercase text-slate-500">{group.label}</p><div className="mt-2 space-y-2">{group.pages.map((page) => <PermissionToggle key={page.id} user={user} page={page} onToggle={onToggle} />)}</div></div>)}
      </div>}
    </section>
  );
}

function PermissionToggle({ user, page, onToggle }) {
  const checked = user.allowed_pages?.includes(page.id);
  return <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"><Checkbox checked={checked} onCheckedChange={(next) => onToggle(user.user_id, page.id, Boolean(next))} data-testid={`permission-${user.user_id}-${page.id}`} /><span className={checked ? "font-bold text-slate-900" : ""}>{page.label}</span>{checked && <Check size={12} className="text-[#0A838A]" />}</label>;
}

function RoleBadge({ role, userId }) {
  const label = role === "super_admin" ? "Super Admin" : role === "admin" ? "Admin" : "User";
  const tone = role === "super_admin" ? "bg-[#0A838A] text-white" : role === "admin" ? "bg-teal-50 text-teal-800 border border-teal-200" : "bg-slate-100 text-slate-600";
  return <span className={`px-2 py-0.5 text-[10px] font-bold ${tone}`} data-testid={`role-badge-${userId}`}>{label}</span>;
}